import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AllItemsPage.module.css';
import IAllItemsPageProps from '../../domain/interfaces/containers/IAllItemsPageProps';
import { Col, Container, Row } from 'react-bootstrap';
import ITraining, {
  ITrainingConsultation,
  ITrainingVideo
} from '../../domain/interfaces/api/ITraining';
import emptyResultAnimation from './empty-results.json';
import Lottie from 'lottie-react';
import TopBar from '../../components/Training/TopBar';
import TrainingItem from '../../components/Training/TrainingItem';
import { ServiceTypeEnum } from '../../domain/enums/ServiceTypeEnum';
import { useHistory, useParams } from 'react-router';
import IMetadata from '../../domain/interfaces/api/IMetadata';
import { SingleTrainingItemLoader } from '../../components/ContentLoader/ContentLoader';
import { getMinPriceByType } from '../../domain/utils';
import './Training.css';
import apiAxios from '../../services/api-axios';

type SortOptionType =
  | 'date-desc'
  | 'date-asc'
  | 'name-asc'
  | 'name-desc'
  | 'price-asc'
  | 'price-desc';

const SORT_OPTIONS: { value: SortOptionType; label: string }[] = [
  { value: 'date-desc', label: 'Najnowsze' },
  { value: 'date-asc', label: 'Najstarsze' },
  { value: 'name-asc', label: 'Nazwa: A do Z' },
  { value: 'name-desc', label: 'Nazwa: Z do A' },
  { value: 'price-asc', label: 'Cena: od najniższej' },
  { value: 'price-desc', label: 'Cena: od najwyższej' }
];

const AllItemsPage: FC<IAllItemsPageProps> = React.memo(
  (props): ReactElement => {
    const { t } = useTranslation();

    const [training, setTraining] = useState<ITraining | null>(null);
    const [metadata, setMetadata] = useState<IMetadata | null>(null);
    const [fetchingData, setFetchingData] = useState<boolean>(false);

    // Stan sortowania (domyślnie 'Najnowsze')
    const [sortOption, setSortOption] = useState<SortOptionType>('date-desc');
    const [isSortOpen, setIsSortOpen] = useState<boolean>(false);
    const sortDropdownRef = useRef<HTMLDivElement | null>(null);

    let { keywords, category, teacher } = useParams<{
      keywords?: string;
      category?: string;
      teacher?: string;
    }>();

    let history = useHistory();

    // Zamykanie dropdowna przy kliknięciu poza jego obszar
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          sortDropdownRef.current &&
          !sortDropdownRef.current.contains(event.target as Node)
        ) {
          setIsSortOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    useEffect(() => {
      fetchData(false, keywords, category, teacher);

      apiAxios
        .getMetadata()
        .then((response) => {
          setMetadata(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = (
      bySearchBar: boolean,
      keywords?: string,
      category?: string,
      teacher?: string
    ) => {
      setFetchingData(true);

      apiAxios
        .getTraining(
          props.type === ServiceTypeEnum.CONSULTANCY,
          props.type === ServiceTypeEnum.VIDEO,
          keywords && keywords !== 'null' ? keywords : undefined,
          category && category !== '-1' ? [category] : undefined,
          teacher && teacher !== '-1' ? [teacher] : undefined
        )
        .then((response) => {
          if (bySearchBar) {
            history.replace({
              pathname: `/${props.type}/${
                keywords || 'null'
              }/${category}/${teacher}`
            });
          }

          setTraining(response.data);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setFetchingData(false);
        });
    };

    const handleSearchAndFlters = (
      searchTerm?: string,
      category?: string,
      teacher?: string
    ) => {
      setTraining(null);
      fetchData(true, searchTerm, category, teacher);
    };

    // Pomocnicze funkcje do wielokryterialnego sortowania
    const getItemTimestamp = (item: any): number => {
      const rawDate =
        item.created_at ??
        item.createdAt ??
        item.date_start ??
        item.date ??
        item.creation_date;

      if (rawDate) {
        const num = Number(rawDate);
        if (!isNaN(num)) {
          return num < 10000000000 ? num * 1000 : num;
        }
        const parsed = new Date(rawDate).getTime();
        if (!isNaN(parsed)) return parsed;
      }
      return Number(item.id) || 0;
    };

    const getItemPrice = (item: any): number => {
      if (item.subscriptions && Array.isArray(item.subscriptions)) {
        const minPrice = getMinPriceByType(item.subscriptions, props.type);
        if (minPrice && typeof minPrice.net_price === 'number') {
          return minPrice.net_price;
        }
        if (minPrice && !isNaN(Number(minPrice.net_price))) {
          return Number(minPrice.net_price);
        }
      }
      return 0;
    };

    const sortedItems = useMemo(() => {
      if (!training || !training[props.type]) return [];
      const list = [...training[props.type]];

      return list.sort((a, b) => {
        switch (sortOption) {
          case 'date-asc':
            return getItemTimestamp(a) - getItemTimestamp(b);
          case 'name-asc':
            return (a.name || '').localeCompare(b.name || '', 'pl');
          case 'name-desc':
            return (b.name || '').localeCompare(a.name || '', 'pl');
          case 'price-asc':
            return getItemPrice(a) - getItemPrice(b);
          case 'price-desc':
            return getItemPrice(b) - getItemPrice(a);
          case 'date-desc':
          default:
            return getItemTimestamp(b) - getItemTimestamp(a);
        }
      });
    }, [training, props.type, sortOption]);

    return (
      <div
        className={`${styles.bgWrapper}`}
        style={{
          backgroundImage: `url(/img/vector-2.png)`
        }}
      >
        <Container>
          <Row className="px-4">
            <h1 className="text-white mb-5 training-title text-end mt-5">
              {props.type === ServiceTypeEnum.VIDEO
                ? t('components.videos.title')
                : t('components.consultancy.title')}
            </h1>
          </Row>
          <Col className="px-4">
            {metadata && (
              <TopBar
                type={props.type}
                handleSearchAndFlters={(
                  searchTerm?: string,
                  category?: string,
                  teacher?: string
                ) => handleSearchAndFlters(searchTerm, category, teacher)}
                categoryItems={metadata?.departments || []}
                teacherItems={metadata?.instructors || []}
                defaultKeyword={keywords}
                defaultCategory={category}
                defaultTeacher={teacher}
              />
            )}

            {/* PASEK SORTOWANIA TYPU E-COMMERCE */}
            {sortedItems.length > 0 && (
              <div className="store-sort-bar">
                <span className="store-results-count">
                  Znaleziono: <strong>{sortedItems.length}</strong>
                </span>

                <div className="store-sort-dropdown-container" ref={sortDropdownRef}>
                  <button
                    type="button"
                    className={`store-sort-button ${isSortOpen ? 'open' : ''}`}
                    onClick={() => setIsSortOpen((prev) => !prev)}
                  >
                    <span className="store-sort-caption">Sortuj po:</span>
                    <span className="store-sort-current-val">
                      {SORT_OPTIONS.find((opt) => opt.value === sortOption)?.label}
                    </span>
                    <svg
                      className={`store-sort-arrow ${isSortOpen ? 'rotated' : ''}`}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {isSortOpen && (
                    <div className="store-sort-menu">
                      {SORT_OPTIONS.map((opt) => (
                        <div
                          key={opt.value}
                          className={`store-sort-option ${
                            sortOption === opt.value ? 'selected' : ''
                          }`}
                          onClick={() => {
                            setSortOption(opt.value);
                            setIsSortOpen(false);
                          }}
                        >
                          <span>{opt.label}</span>
                          {sortOption === opt.value && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Col>
          <Row className="mt-4 position-relative">
            {sortedItems && sortedItems.length > 0 ? (
              sortedItems.map(
                (value: ITrainingConsultation | ITrainingVideo) => {
                  return (
                    <Col
                      key={value.id}
                      md={6}
                      lg={4}
                      className="mb-4 d-flex justify-content-center flex-grow-1 training-item-wrapper"
                    >
                      <TrainingItem element={value} type={props.type} />
                    </Col>
                  );
                }
              )
            ) : fetchingData ? (
              <SingleTrainingItemLoader />
            ) : (
              <Col xs={12} lg={6}>
                <h1 className="empty-results">
                  {' '}
                  {t('global.filter_no_results')}
                </h1>
                <Lottie animationData={emptyResultAnimation} loop autoplay />
              </Col>
            )}
          </Row>
        </Container>
      </div>
    );
  }
);

export default AllItemsPage;